/**
 * Client-side green-screen removal for the LiveAvatar video stream.
 *
 * The LiveAvatar API has no background parameter — every avatar streams on a
 * solid green background, and HeyGen's official answer is client-side chroma
 * keying (their web-sdk ships this exact HSV keyer in apps/bg-removal-demo).
 * The raw <video> keeps playing hidden while keyed frames with transparent
 * background are drawn onto a canvas that carries the same layout classes.
 */

// HeyGen's defaults from the SDK bg-removal demo.
export const DEFAULT_CHROMA_KEY_OPTIONS = {
  minHue: 60,
  maxHue: 180,
  minSaturation: 0.1,
  threshold: 1.0,
};

// Key at most this many pixels on the long side — the avatar panel renders at
// 160–320px, so keying the full 720p stream would waste mobile CPU.
const MAX_KEY_DIM = 480;

/** Set the alpha of green-screen pixels to 0 in-place (RGBA byte array). */
export function keyOutGreen(data, options = DEFAULT_CHROMA_KEY_OPTIONS) {
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const delta = max - min;

    let h = 0;
    if (delta === 0) {
      h = 0;
    } else if (max === r) {
      h = ((g - b) / delta) % 6;
    } else if (max === g) {
      h = (b - r) / delta + 2;
    } else {
      h = (r - g) / delta + 4;
    }
    h = Math.round(h * 60);
    if (h < 0) h += 360;

    const s = max === 0 ? 0 : delta / max;
    const v = max / 255;

    const isGreen =
      h >= options.minHue &&
      h <= options.maxHue &&
      s > options.minSaturation &&
      v > 0.15 &&
      g > r * options.threshold &&
      g > b * options.threshold;

    if (isGreen) {
      // Feather the edges: mostly-green pixels go fully transparent, spill
      // pixels (hair edges) get partial alpha.
      const greenness = (g - Math.max(r, b)) / (g || 1);
      const alphaValue = Math.max(0, 1 - greenness * 4);
      data[i + 3] = alphaValue < 0.2 ? 0 : Math.round(alphaValue * 255);
    }
  }
}

/**
 * Continuously draw keyed frames from `video` onto `canvas`.
 * Returns a stop function, or null when 2D canvas isn't available (caller
 * should then fall back to showing the raw video).
 */
export function setupChromaKey(video, canvas, options = DEFAULT_CHROMA_KEY_OPTIONS) {
  const ctx = canvas.getContext('2d', { willReadFrequently: true, alpha: true });
  if (!ctx) return null;

  let raf = null;
  const render = () => {
    raf = requestAnimationFrame(render);
    if (video.readyState < 2 || !video.videoWidth) return;
    const scale = Math.min(1, MAX_KEY_DIM / Math.max(video.videoWidth, video.videoHeight));
    const w = Math.round(video.videoWidth * scale);
    const h = Math.round(video.videoHeight * scale);
    if (canvas.width !== w) canvas.width = w;
    if (canvas.height !== h) canvas.height = h;
    ctx.drawImage(video, 0, 0, w, h);
    const frame = ctx.getImageData(0, 0, w, h);
    keyOutGreen(frame.data, options);
    ctx.putImageData(frame, 0, 0);
  };
  render();

  return () => {
    if (raf !== null) cancelAnimationFrame(raf);
  };
}
